import type { GridValidationStatus, LocalFormItem } from 'types/agendaForm';

export const getRange = (val: string | undefined | null): { start: number; end: number } => {
  if (val === undefined || val === null) return { start: 0, end: 0 };
  const str = String(val);
  if (str.includes('-')) {
    const parts = str.split('-').map(Number);
    const start = Number.isFinite(parts[0]) ? parts[0] : 0;
    const end = Number.isFinite(parts[1]) ? parts[1] : start;
    return { start, end: end >= start ? end : start };
  }
  const num = Number(val);
  return { start: Number.isFinite(num) ? num : 0, end: Number.isFinite(num) ? num : 0 };
};

export const getStartPos = (val: string | undefined | null) => getRange(val).start;

export const getSpan = (val: string | undefined | null): number => {
  const { start, end } = getRange(val);
  return end - start + 1;
};

export const createPosString = (start: number, span: number): string => {
  if (!Number.isFinite(start) || !Number.isFinite(span) || span <= 0) return String(start);
  if (span === 1) return String(start);
  return `${start}-${start + span - 1}`;
};

const checkAreaOverlapWithBase = (
  items: LocalFormItem[],
  rect: { x: number; y: number; w: number; h: number },
  gridSize: { x: number; y: number },
  ignoredItemId: string | number | null,
  coordinateBase: 0 | 1
): { isCollision: boolean; status: GridValidationStatus; overlappingItems: LocalFormItem[] } => {
  const { x, y, w, h } = rect;

  if (x < coordinateBase || y < coordinateBase || x + w > gridSize.x + coordinateBase || y + h > gridSize.y + coordinateBase) {
    return {
      isCollision: true,
      status: 'OUT_OF_BOUNDS',
      overlappingItems: [],
    };
  }

  const rectLeft = x;
  const rectRight = x + w - 1;
  const rectTop = y;
  const rectBottom = y + h - 1;

  const overlappingItems = items.filter((item) => {
    if (item.isEmpty) return false;

    const itemId = item.data?.id ?? item.id;
    if (ignoredItemId && String(itemId) === String(ignoredItemId)) return false;

    const itemX = getRange(item.x);
    const itemY = getRange(item.y);

    const overlapX = rectLeft <= itemX.end && rectRight >= itemX.start;
    const overlapY = rectTop <= itemY.end && rectBottom >= itemY.start;

    return overlapX && overlapY;
  });

  if (overlappingItems.length === 0) {
    return { isCollision: false, status: 'VALID', overlappingItems: [] };
  }

  return {
    isCollision: true,
    status: overlappingItems.length > 1 ? 'COLLISION_MULTIPLE' : 'COLLISION_SINGLE',
    overlappingItems,
  };
};

export const checkAreaOverlap = (
  items: LocalFormItem[],
  rect: { x: number; y: number; w: number; h: number },
  gridSize: { x: number; y: number },
  ignoredItemId: string | number | null
) => checkAreaOverlapWithBase(items, rect, gridSize, ignoredItemId, 0);

export const checkOneBasedAreaOverlap = (
  items: LocalFormItem[],
  rect: { x: number; y: number; w: number; h: number },
  gridSize: { x: number; y: number },
  ignoredItemId: string | number | null
) => checkAreaOverlapWithBase(items, rect, gridSize, ignoredItemId, 1);

export const validateSwap = (sourceSpan: { x: number; y: number }, targetItem: LocalFormItem): { isValid: boolean; status: GridValidationStatus } => {
  const targetSpanX = getSpan(targetItem.x);
  const targetSpanY = getSpan(targetItem.y);

  if (sourceSpan.x !== targetSpanX || sourceSpan.y !== targetSpanY) {
    return { isValid: false, status: 'SIZE_MISMATCH' };
  }

  return { isValid: true, status: 'VALID' };
};

const checkDragValidityWithBase = (
  items: LocalFormItem[],
  candidate: { x: number; y: number; w: number; h: number },
  ignoredIdentifier: string | null,
  gridSize: { x: number; y: number },
  coordinateBase: 0 | 1
): { isValid: boolean; status: GridValidationStatus } => {
  const mappedItems = items.map((i) => ({
    ...i,
    id: i.identifier,
    data: null,
  }));

  const overlapCheck = checkAreaOverlapWithBase(mappedItems, candidate, gridSize, ignoredIdentifier, coordinateBase);

  if (overlapCheck.status !== 'COLLISION_SINGLE') {
    return {
      isValid: overlapCheck.status === 'VALID',
      status: overlapCheck.status,
    };
  }

  const target = overlapCheck.overlappingItems[0];
  return validateSwap({ x: candidate.w, y: candidate.h }, target);
};

export const checkDragValidity = (
  items: LocalFormItem[],
  candidate: { x: number; y: number; w: number; h: number },
  ignoredIdentifier: string | null,
  gridSize: { x: number; y: number }
) => checkDragValidityWithBase(items, candidate, ignoredIdentifier, gridSize, 0);

export const checkOneBasedDragValidity = (
  items: LocalFormItem[],
  candidate: { x: number; y: number; w: number; h: number },
  ignoredIdentifier: string | null,
  gridSize: { x: number; y: number }
) => checkDragValidityWithBase(items, candidate, ignoredIdentifier, gridSize, 1);

export const shiftCoord = (val: string, deletedIndex: number): string => {
  if (deletedIndex < 0) return val;
  const { start, end } = getRange(val);

  if (end < deletedIndex) return val;

  if (start > deletedIndex) {
    const newStart = start - 1;
    const newEnd = end - 1;
    return newStart === newEnd ? String(newStart) : `${newStart}-${newEnd}`;
  }

  // deletedIndex falls inside the range — shrink the range
  if (start <= deletedIndex && end >= deletedIndex) {
    const newEnd = end - 1;
    if (newEnd < start) return String(start);
    return newEnd === start ? String(start) : `${start}-${newEnd}`;
  }

  return val;
};

export const shiftCoordUp = (val: string, amount: number): string => {
  const { start, end } = getRange(val);
  const newStart = start + amount;
  const newEnd = end + amount;
  return newStart === newEnd ? String(newStart) : `${newStart}-${newEnd}`;
};

const findFirstEmptyIndexWithBase = (size: number, checkIsOccupied: (index: number) => boolean, coordinateBase: 0 | 1): number => {
  if (size <= 0) return -1;
  return Array.from({ length: size }, (_, i) => i + coordinateBase).find((index) => !checkIsOccupied(index)) ?? -1;
};

const findLastEmptyIndexWithBase = (size: number, checkIsOccupied: (index: number) => boolean, coordinateBase: 0 | 1): number => {
  if (size <= 0) return -1;
  return Array.from({ length: size }, (_, i) => size - 1 + coordinateBase - i).find((index) => !checkIsOccupied(index)) ?? -1;
};

export const findFirstEmptyIndex = (size: number, checkIsOccupied: (index: number) => boolean): number => {
  return findFirstEmptyIndexWithBase(size, checkIsOccupied, 0);
};

export const findLastEmptyIndex = (size: number, checkIsOccupied: (index: number) => boolean): number => {
  return findLastEmptyIndexWithBase(size, checkIsOccupied, 0);
};

export const findFirstOneBasedEmptyIndex = (size: number, checkIsOccupied: (index: number) => boolean): number => {
  return findFirstEmptyIndexWithBase(size, checkIsOccupied, 1);
};

export const findLastOneBasedEmptyIndex = (size: number, checkIsOccupied: (index: number) => boolean): number => {
  return findLastEmptyIndexWithBase(size, checkIsOccupied, 1);
};

export const generateGridPlaceholders = <T extends { x: string; y: string; subTabId?: string | null; name?: string }>(
  realItems: T[],
  sizeX: number,
  sizeY: number,
  template: T,
  subTabIds?: string[]
): T[] => {
  const subTabGroups = new Map<string | null, T[]>();
  for (const item of realItems) {
    const itemSubTabId = item.subTabId ?? null;
    if (!subTabGroups.has(itemSubTabId)) {
      subTabGroups.set(itemSubTabId, []);
    }
    subTabGroups.get(itemSubTabId)!.push(item);
  }

  for (const id of subTabIds ?? []) {
    if (!subTabGroups.has(id)) {
      subTabGroups.set(id, []);
    }
  }

  if ((!subTabIds || subTabIds.length === 0) && !subTabGroups.has(null)) {
    subTabGroups.set(null, []);
  }

  const allItems: T[] = [];
  for (const [groupSubTabId, groupItems] of subTabGroups) {
    const isDefaultGroup = groupSubTabId === null;

    if (groupItems.length === 0) {
      // Empty default group: generate full sizeX × sizeY placeholder grid.
      // Empty sub-tab group: a single placeholder is sufficient.
      if (isDefaultGroup) {
        const emptyOccupied = new Set<string>();
        const placeholders = Array.from({ length: Math.max(1, sizeX) * Math.max(1, sizeY) }, (_, index) => {
          const x = index % Math.max(1, sizeX);
          const y = Math.floor(index / Math.max(1, sizeX));
          return { x, y };
        })
          .filter(({ x, y }) => !emptyOccupied.has(`${x},${y}`))
          .map(({ x, y }) => ({ ...template, x: String(x), y: String(y), subTabId: groupSubTabId }));
        allItems.push(...placeholders);
      } else {
        allItems.push({ ...template, x: '0', y: '0', subTabId: groupSubTabId });
      }
      continue;
    }

    // Compute effective grid dimensions for this sub-tab group based on
    // the real items it contains. For sub-tab groups (non-null subTabId),
    // we do NOT use the section's full sizeX/sizeY as a floor — this prevents
    // a sub-tab with a single item from generating a huge grid of placeholders.
    // The default group (null) still uses sizeX/sizeY as a floor so the main
    // section grid renders correctly.
    let groupMaxX = 0;
    let groupMaxY = 0;
    for (const item of groupItems) {
      const xRange = getRange(item.x);
      const yRange = getRange(item.y);
      groupMaxX = Math.max(groupMaxX, xRange.end + 1);
      groupMaxY = Math.max(groupMaxY, yRange.end + 1);
    }
    const effectiveSizeX = isDefaultGroup ? Math.max(1, groupMaxX, sizeX) : Math.max(1, groupMaxX);
    const effectiveSizeY = isDefaultGroup ? Math.max(1, groupMaxY, sizeY) : Math.max(1, groupMaxY);

    // Mark every occupied cell (not just start positions) to avoid
    // placeholders overlapping multi-cell items.
    const occupiedCells = new Set<string>();
    for (const item of groupItems) {
      const xRange = getRange(item.x);
      const yRange = getRange(item.y);
      for (let x = xRange.start; x <= xRange.end; x++) {
        for (let y = yRange.start; y <= yRange.end; y++) {
          occupiedCells.add(`${x},${y}`);
        }
      }
    }

    const newPlaceholders = Array.from({ length: effectiveSizeX * effectiveSizeY }, (_, index) => {
      const x = index % effectiveSizeX;
      const y = Math.floor(index / effectiveSizeX);
      return { x, y };
    })
      .filter(({ x, y }) => !occupiedCells.has(`${x},${y}`))
      .map(({ x, y }) => ({
        ...template,
        x: String(x),
        y: String(y),
        subTabId: groupSubTabId,
      }));

    allItems.push(...groupItems, ...newPlaceholders);
  }

  return allItems;
};
